import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { chain, get, last } from 'lodash';
import { MenuItem, Select, Typography } from '@mui/material';

import { ParentPaper } from '../atoms/parent-paper';
import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { SolarPanel } from '../atoms/icons/solar-panel';
import { ControlBox } from '../atoms/control-box';
import { ChartBox, Variant } from '../molecules/chart-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Group } from '../organisms/group';
import { useSelector } from '../store/utils';
import { FormattedStatus } from '../store/formatted-status';
import { useFormatNumber } from '../utils/format-number';
import { Inverter } from '../../../core/src/shared/enums';
import { emsModeOptions, subInverterNameMapping } from '../utils/ems';

const Ems = () => {
  const [selectedInverterState, setSelectedInverterState] = useState<
    undefined | [string, number]
  >();

  const formatNumber = useFormatNumber();

  const selectInverterOptions = useSelector(({ variables }) => {
    return chain(Object.entries(variables.INVERTER_CONFIG))
      .filter(([_, { type }]) => {
        return [Inverter.SolaxEMS1000PRO, Inverter.SolaxEMS1000].includes(type);
      })
      .map(([key, config]) => {
        if (
          config.type !== Inverter.SolaxEMS1000 &&
          config.type !== Inverter.SolaxEMS1000PRO
        ) {
          throw new Error(`Unexpected inverter type: ${config.type}`);
        }

        return new Array(config.inverters?.length ?? 0)
          .fill(null)
          .map((_, subInverterIndex) => {
            const subInverter = variables.INVERTER_STATS[key].inverterDetail?.[
              subInverterIndex
            ] ?? { name: 0 };

            return [
              key,
              subInverterIndex,
              { type: config.type, subInverterName: subInverter.name },
            ] as const;
          });
      })
      .flatten()
      .value();
  });

  const [
    selectedInverterType,
    selectedInverterMode,
    selectedInverterDetail,
    selectedInverterIndex,
    selectedSubInverterIndex,
  ] = useSelector(({ variables }) => {
    if (!selectInverterOptions.length) {
      return [undefined, undefined, undefined, undefined, undefined] as const;
    }

    const [key, subIndex] = selectedInverterState ?? selectInverterOptions[0];
    const detail =
      variables.INVERTER_STATS[key].inverterDetail?.[subIndex] ?? null;

    return [
      variables.INVERTER_CONFIG[key].type,
      variables.INVERTER_STATS[key].mode,
      detail,
      key,
      subIndex,
    ] as const;
  });

  if (!selectInverterOptions.length) {
    return null;
  }

  return (
    <>
      <Group layout="list">
        <ControlBox
          title={<FormattedMessage id="ems.inverter" defaultMessage="Měnič" />}
          fullWidth
          control={
            <Select
              value={JSON.stringify([
                selectedInverterIndex,
                selectedSubInverterIndex,
              ])}
              onChange={(e) =>
                setSelectedInverterState(JSON.parse(e.target.value))
              }
            >
              {selectInverterOptions.map(
                ([key, subIndex, { type, subInverterName }]) => {
                  const message = get(subInverterNameMapping, [
                    type,
                    subInverterName,
                  ]);
                  return (
                    <MenuItem
                      value={JSON.stringify([key, subIndex])}
                      key={`${key}-${subIndex}`}
                    >
                      {message ? <FormattedMessage {...message} /> : '---'}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          }
        />

        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="photovoltaics.status"
                defaultMessage="Režim FVE"
              />
            }
            subtitle={<FormattedStatus type="photovoltaic" />}
            fullWidth
          />
          {typeof selectedInverterMode === 'number' && (
            <SwitchBox
              title={
                <FormattedMessage id="ems.mode" defaultMessage="Režim EMS" />
              }
              subtitle={<>{get(emsModeOptions, selectedInverterMode, '---')}</>}
              fullWidth
            />
          )}
        </ParentPaper>
      </Group>

      {selectedInverterType && selectedInverterDetail && (
        <Group layout="list">
          <SwitchBox
            title={
              <FormattedMessage
                id="ems.inverter.name"
                defaultMessage="Název střídače"
              />
            }
            subtitle={(() => {
              const message = get(subInverterNameMapping, [
                selectedInverterType,
                selectedInverterDetail?.name,
              ]);
              return message ? <FormattedMessage {...message} /> : '---';
            })()}
            fullWidth
          />
        </Group>
      )}

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="photovoltaics.statistics"
            defaultMessage="Výkon elektrárny"
          />
        }
      >
        <ChartBox
          variableId="PV_POWER_STRING_GRAPH"
          variableSuffix={`${selectedInverterIndex}||${selectedSubInverterIndex}`}
          initialVariant={Variant.LineStacked}
          getSubStatsLabel={(key) => {
            const rawStringNumber = last(key.split('-'));
            const stringNumber = Number(rawStringNumber);
            return `String ${
              Number.isNaN(stringNumber) ? '-' : stringNumber + 1
            }`;
          }}
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="ems.details"
            defaultMessage="Parametry měniče"
          />
        }
      >
        {selectedInverterDetail && (
          <>
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="ems.power.total"
                  defaultMessage="Celkový výkon"
                />
              }
              value={formatNumber(
                (selectedInverterDetail.power ?? []).reduce(
                  (a, b) => a + (b ?? 0),
                  0
                ),
                {
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }
              )}
              icon={<SolarPanel />}
              expanded={
                <Typography
                  variant="body1"
                  sx={{ color: 'text.disabled', mt: 1 }}
                >
                  {Array.from({ length: 6 }, (_, i) => (
                    <React.Fragment key={i}>
                      <FormattedMessage
                        id="ems.power.index"
                        defaultMessage="Výkon string {index}"
                        values={{ index: i + 1 }}
                      />
                      :{' '}
                      {formatNumber(selectedInverterDetail.power?.[i] ?? 0, {
                        style: 'unit',
                        unit: 'kilowatt',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                      {i < 5 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
              }
            />
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="ems.activePower"
                  defaultMessage="Činný výkon"
                />
              }
              value={formatNumber(selectedInverterDetail.activePower ?? 0, {
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
              icon={<ElectricalGrid />}
            />
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="ems.reactivePower"
                  defaultMessage="Jalový výkon"
                />
              }
              value={formatNumber(selectedInverterDetail.reactivePower ?? 0, {
                style: 'unit',
                unit: 'kilovoltampere-reactive',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
              icon={<ElectricalGrid />}
            />
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="ems.apparentPower"
                  defaultMessage="Zdánlivý výkon"
                />
              }
              value={formatNumber(selectedInverterDetail.apparentPower ?? 0, {
                style: 'unit',
                unit: 'kilovoltampere',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
              icon={<ElectricalGrid />}
            />
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="ems.powerFactor"
                  defaultMessage="Účiník"
                />
              }
              value={formatNumber(selectedInverterDetail.powerFactor ?? 0, {
                style: undefined,
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
              icon={<ElectricalGrid />}
            />
          </>
        )}
      </Group>
    </>
  );
};

export default Ems;
